import { createFileRoute, redirect } from '@tanstack/react-router';
import { parse, object, optional, picklist, string } from 'valibot';

import { medplum } from '@/config/medplum';
import { queryClient } from '@/config/queryClient';
import { queryKeys } from '@/lib/query-keys';

const tabOptions = ['demographics', 'conditions', 'insurance', 'history'] as const;

export const Route = createFileRoute('/(authenticated)/referrals/$id')({
  validateSearch: (search) => {
    return parse(
      object({
        tab: optional(picklist(tabOptions), 'demographics'),
        program_id: optional(string()),
      }),
      search,
    );
  },
  beforeLoad: async ({ params, search }) => {
    const serviceRequestId = params.id;

    const serviceRequest = await queryClient.ensureQueryData({
      queryKey: queryKeys.serviceRequest.detail(serviceRequestId),
      queryFn: () => medplum.readResource('ServiceRequest', serviceRequestId),
    });

    const patientRef = serviceRequest.subject?.reference;
    if (!patientRef) return;

    const patientId = patientRef.replace('Patient/', '');

    const searchParams = {
      subject: `Patient/${patientId}`,
      _sort: '-authored',
      _count: '50',
    };

    const bundle = await queryClient.ensureQueryData({
      queryKey: queryKeys.serviceRequest.list(searchParams),
      queryFn: () => medplum.search('ServiceRequest', searchParams),
    });

    if (!bundle.entry || bundle.entry.length === 0) {
      return;
    }

    const availablePrograms = Array.from(
      bundle.entry.reduce((programs, entry) => {
        const code = entry.resource?.code?.coding?.[0]?.code;
        if (code) {
          programs.add(code);
        }
        return programs;
      }, new Set<string>()),
    );

    if (availablePrograms.length === 0) {
      return;
    }

    if (!search.program_id || !availablePrograms.includes(search.program_id)) {
      throw redirect({
        to: '/referrals/$id',
        params: { id: params.id },
        search: { ...search, program_id: availablePrograms[0] },
        replace: true,
      });
    }
  },
});
